/**
 * 实时同步服务
 * 使用 Supabase Realtime 订阅数据变化，自动刷新缓存
 */

import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';
import * as groupCache from '../group/groupCache';

// 订阅通道管理
const channels: Map<string, any> = new Map();
// 缓存的 supabase 实例（用于取消订阅）
let cachedSupabase: any = null;

/**
 * 订阅分组变化
 */
export async function subscribeToGroups(
  onUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void
): Promise<() => void> {
  if (!isSupabaseAvailable() || !supabase) {
    console.warn('[RealtimeSync] Supabase 未配置，无法订阅分组变化');
    return () => {};
  }

  cachedSupabase = supabase;
  const channelName = 'groups_changes';
  
  // 如果已存在订阅，先取消
  if (channels.has(channelName) && cachedSupabase) {
    cachedSupabase.removeChannel(channels.get(channelName));
  }

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'groups',
      },
      async (payload) => {
        console.log('[RealtimeSync] 分组变化:', payload.eventType, payload.new || payload.old);
        
        // 刷新分组列表缓存
        await groupCache.clearUserGroupsCache();
        
        // 如果用户当前在查看该分组，刷新成员列表缓存
        const groupId = (payload.new as any)?.id || (payload.old as any)?.id;
        if (groupId) {
          await groupCache.clearGroupMembersCache(groupId);
        }
        
        // 触发回调
        if (onUpdate) {
          onUpdate(payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE', (payload.new || payload.old) as any);
        }
      }
    )
    .subscribe();

  channels.set(channelName, channel);

  return () => {
    if (cachedSupabase) {
      cachedSupabase.removeChannel(channel);
    }
    channels.delete(channelName);
  };
}

/**
 * 订阅分组成员变化
 */
export async function subscribeToGroupMembers(
  onUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void
): Promise<() => void> {
  if (!isSupabaseAvailable() || !supabase) {
    console.warn('[RealtimeSync] Supabase 未配置，无法订阅成员变化');
    return () => {};
  }

  cachedSupabase = supabase;
  const channelName = 'group_members_changes';
  
  // 如果已存在订阅，先取消
  if (channels.has(channelName) && cachedSupabase) {
    cachedSupabase.removeChannel(channels.get(channelName));
  }

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'group_members',
      },
      async (payload) => {
        console.log('[RealtimeSync] 成员变化:', payload.eventType, payload.new || payload.old);
        
        const groupId = (payload.new as any)?.group_id || (payload.old as any)?.group_id;
        if (groupId) {
          // 刷新该分组的成员列表缓存
          await groupCache.clearGroupMembersCache(groupId);
          
          // 刷新分组列表缓存（因为成员变化可能影响分组显示）
          await groupCache.clearUserGroupsCache();
        }
        
        // 触发回调
        if (onUpdate) {
          onUpdate(payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE', (payload.new || payload.old) as any);
        }
      }
    )
    .subscribe();

  channels.set(channelName, channel);

  return () => {
    if (cachedSupabase) {
      cachedSupabase.removeChannel(channel);
    }
    channels.delete(channelName);
  };
}

/**
 * 订阅分组邀请变化
 */
export async function subscribeToGroupInvitations(
  onUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void
): Promise<() => void> {
  if (!isSupabaseAvailable() || !supabase) {
    console.warn('[RealtimeSync] Supabase 未配置，无法订阅邀请变化');
    return () => {};
  }

  cachedSupabase = supabase;
  const channelName = 'group_invitations_changes';
  
  // 如果已存在订阅，先取消
  if (channels.has(channelName) && cachedSupabase) {
    cachedSupabase.removeChannel(channels.get(channelName));
  }

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'group_invitations',
      },
      async (payload) => {
        console.log('[RealtimeSync] 邀请变化:', payload.eventType, payload.new || payload.old);
        
        const groupId = (payload.new as any)?.group_id || (payload.old as any)?.group_id;
        if (groupId) {
          // 邀请变化可能影响成员列表（接受邀请后）
          await groupCache.clearGroupMembersCache(groupId);
        }
        
        // 触发回调
        if (onUpdate) {
          onUpdate(payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE', (payload.new || payload.old) as any);
        }
      }
    )
    .subscribe();

  channels.set(channelName, channel);

  return () => {
    if (cachedSupabase) {
      cachedSupabase.removeChannel(channel);
    }
    channels.delete(channelName);
  };
}

/**
 * 订阅待办事项变化
 */
export async function subscribeToTodos(
  onUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void
): Promise<() => void> {
  if (!isSupabaseAvailable() || !supabase) {
    console.warn('[RealtimeSync] Supabase 未配置，无法订阅待办事项变化');
    return () => {};
  }

  cachedSupabase = supabase;
  const channelName = 'todos_changes';
  
  // 如果已存在订阅，先取消
  if (channels.has(channelName) && cachedSupabase) {
    cachedSupabase.removeChannel(channels.get(channelName));
  }

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'todos',
        filter: (() => {
          // 只接收当前用户相关的待办事项
          // RLS 策略会自动过滤，但这里也可以添加额外过滤
          return undefined;
        })(),
      },
      async (payload) => {
        console.log('[RealtimeSync] 待办事项变化:', payload.eventType, payload.new || payload.old);
        
        // 清除待办事项缓存（使用通用的缓存键）
        // 注意：这里需要导入缓存策略，但为了避免循环依赖，我们直接清除分组缓存
        // 待办事项的缓存清除应该在调用方处理
        
        const groupId = (payload.new as any)?.group_id || (payload.old as any)?.group_id;
        if (groupId) {
          // 如果待办事项属于某个分组，清除该分组的缓存
          await groupCache.clearGroupMembersCache(groupId);
        }
        
        // 触发回调
        if (onUpdate) {
          onUpdate(payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE', (payload.new || payload.old) as any);
        }
      }
    )
    .subscribe();

  channels.set(channelName, channel);

  return () => {
    if (cachedSupabase) {
      cachedSupabase.removeChannel(channel);
    }
    channels.delete(channelName);
  };
}

/**
 * 订阅所有相关变化
 */
export async function subscribeToAll(
  callbacks?: {
    onGroupsUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void;
    onMembersUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void;
    onInvitationsUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void;
    onTodosUpdate?: (event: 'INSERT' | 'UPDATE' | 'DELETE', data: any) => void;
  }
): Promise<() => void> {
  const unsubscribers: (() => void)[] = [];

  unsubscribers.push(await subscribeToGroups(callbacks?.onGroupsUpdate));
  unsubscribers.push(await subscribeToGroupMembers(callbacks?.onMembersUpdate));
  unsubscribers.push(await subscribeToGroupInvitations(callbacks?.onInvitationsUpdate));
  unsubscribers.push(await subscribeToTodos(callbacks?.onTodosUpdate));

  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
}

/**
 * 取消所有订阅
 */
export async function unsubscribeAll(): Promise<void> {
  if (!isSupabaseAvailable() || !supabase) {
    return;
  }
  channels.forEach((channel) => {
    if (supabase) supabase.removeChannel(channel);
  });
  channels.clear();
  cachedSupabase = null;
}
